import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { UserModel } from '@app/core/model/user.model';
import { AnnouncementListService } from '@app/core/service/announcement-list.service';
import { AttendanceBonusModel } from '@app/core/service/api/attendanceBonus.api.service';
import { IntegrationApiService } from '@app/core/service/api/integration.api.service';
import { ApprovalsListService } from '@app/core/service/approvals-list.service';
import { AttendanceApprovalService } from '@app/core/service/attendance-approval.service';
import { AttendanceBonusListService } from '@app/core/service/attendance-bonus-list.service';
import { EnvironmentService } from '@app/core/service/environment.service';
import { GoogleReviewsCountService } from '@app/core/service/google-reviews-count.service';
import { TenantRouteContextService } from '@app/core/service/tenant-route-context.service';
import { TenantService } from '@app/core/service/tenant.service';
import { UserEntityService } from '@app/core/service/user-entity.service';
import { FcmService } from '@app/fcm/fcm.service';
import { GlobalSettings } from '@app/types/global-settings';
import { deriveRoleAccess } from '@app/utils/roles';
import { EIntegrationProvider } from '@backend/models/types/integration';
import { ITenantFeatures } from '@backend/models/types/tenant';
import { App } from '@capacitor/app';
import { IonMenu } from '@ionic/angular';
import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  iif,
  map,
  of,
  shareReplay,
  switchMap
} from 'rxjs';

export enum ESubmenu {
  PERFORMANCE = 'PERFORMANCE',
  SETTINGS = 'SETTINGS'
}

const performanceSubmenuRoutes = [
  'performance',
  'psychological-survey-feedback',
  'approvals',
  'attendance-approvals',
  'google-reviews'
];

@Component({
  selector: 'app-full-layout',
  templateUrl: './full.component.html',
  styleUrls: ['./full.component.scss'],
  animations: [
    trigger('submenuInOut', [
      state(
        'in',
        style({
          height: '*'
        })
      ),
      state(
        'out',
        style({
          height: '0px'
        })
      ),
      transition('in => out', animate('250ms cubic-bezier(0.22, 1, 0.36, 1)')),
      transition('out => in', animate('250ms cubic-bezier(0.22, 1, 0.36, 1)'))
    ])
  ],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false
})
export class FullComponent implements OnInit {
  @ViewChild('mobileMenu', { static: true })
  public mobileMenu!: IonMenu;

  protected readonly openedSubmenuType = ESubmenu;

  protected currentUser$: Observable<UserModel> = this._userEntityService.user$;
  protected userRoleStatus$ = this._userEntityService.user$.pipe(
    map((user) => deriveRoleAccess(user?.roles)),
    shareReplay(1)
  );
  protected isSuperAdmin: boolean = false;

  // protected receivedInPeriod$: Observable<UserPointsRecord> =
  //   this._userEntityService.receivedInPeriod$;
  // protected teamAverageInPeriod$: Observable<{ teamAveragePoints: number }> =
  //   this._userEntityService.teamAverageInPeriod$;
  protected unreadAnnouncementsCount$: Observable<number> =
    this._announcementListService.unacknowledgedCount$;
  protected tenantFeatures: Pick<
    ITenantFeatures,
    | 'checklist'
    | 'tags'
    | 'insights'
    | 'dailyReport'
    | 'surveyFeedback'
    | 'announcements'
    | 'survey'
    | 'library'
    | 'suggestions'
    | 'googleReviews'
    | 'calculateRewards'
  > = {
    checklist: false,
    tags: false,
    announcements: false,
    survey: false,
    surveyFeedback: false,
    insights: false,
    dailyReport: false,
    library: false,
    suggestions: false,
    googleReviews: false,
    calculateRewards: false
  };
  protected openedMenu: ESubmenu = null;
  protected closedMenu: ESubmenu = null;
  protected waitingForApprovalsTaskPerformances$ = this.userRoleStatus$.pipe(
    switchMap(({ isAdminOrManager }) =>
      iif(() => isAdminOrManager, this._approvalsListService.approvals$, of([]))
    )
  );
  protected pendingGoogleReviewsCount$: Observable<number> =
    this._googleReviewsCountService.pendingCount$;
  protected readonly attendanceApprovalsEnabled$: Observable<{
    value: boolean;
  }> = combineLatest([
    this._tenantService.tenant$,
    this._integrationApi.tenantIntegrations$,
    this.userRoleStatus$
  ]).pipe(
    map(([tenant, integrations, { isAdminOrManager }]) => {
      const integrationsArray = Array.isArray(integrations)
        ? integrations
        : ((integrations ?? []) as any[]);

      return {
        value:
          this._attendanceApprovalService.isAttendanceApprovalEnabled(
            tenant,
            integrationsArray
          ) && isAdminOrManager
      };
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  protected pendingAttendanceBonusesLength$: Observable<number> =
    this.attendanceApprovalsEnabled$.pipe(
      switchMap(({ value: attendanceApprovalsEnabled }) =>
        iif(
          () => attendanceApprovalsEnabled,
          this._attendanceBonusListService.pendingAttendanceBonuses$,
          of([])
        )
      ),
      map((bonuses: AttendanceBonusModel[]) => bonuses.length)
    );
  protected googleReviewsEnabled$: Observable<{ value: boolean }> =
    combineLatest([
      this._tenantService.tenant$,
      this._integrationApi.tenantIntegrations$,
      this.userRoleStatus$
    ]).pipe(
      map(([tenant, integrations, { isAdminOrManager }]) => {
        const integrationsArray = Array.isArray(integrations)
          ? integrations
          : ((integrations ?? []) as any[]);

        const hasGoogleIntegration = integrationsArray.some(
          (integration) =>
            integration.provider === EIntegrationProvider.GOOGLE_MY_BUSINESS &&
            !integration.isInitializing
        );

        return {
          value:
            !!tenant?.features?.googleReviews &&
            hasGoogleIntegration &&
            isAdminOrManager
        };
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  protected readonly hasAnyPerformanceAttention$: Observable<{
    value: boolean;
  }> = combineLatest([
    this.waitingForApprovalsTaskPerformances$,
    this.pendingAttendanceBonusesLength$
  ]).pipe(
    map(([taskPerformances, attendanceBonusesLength]) => ({
      value: taskPerformances.length > 0 || attendanceBonusesLength > 0
    }))
  );
  protected readonly tenantLoading$ = combineLatest([
    this._tenantRouteContext.tenantLoading$,
    this._tenantRouteContext.tenantSwitching$
  ]).pipe(
    map(([isTenantLoading, isTenantSwitching]) => {
      return isTenantLoading || isTenantSwitching;
    }),
    distinctUntilChanged()
  );
  protected readonly tenant$ = this._tenantService.tenant$;

  public constructor(
    private readonly _authenticationService: AuthenticationService,
    private readonly _fcmService: FcmService,
    private readonly _userEntityService: UserEntityService,
    private readonly _announcementListService: AnnouncementListService,
    private readonly _tenantRouteContext: TenantRouteContextService,
    private readonly _tenantService: TenantService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _approvalsListService: ApprovalsListService,
    private readonly _attendanceBonusListService: AttendanceBonusListService,
    private readonly _integrationApi: IntegrationApiService,
    private readonly _attendanceApprovalService: AttendanceApprovalService,
    private readonly _googleReviewsCountService: GoogleReviewsCountService
  ) {
    this._fcmService.lastPushNotification$.subscribe(() => {
      this.mobileMenu.close();
    });
    const globalSettings: GlobalSettings =
      this._activatedRoute.snapshot.data.globalSettings;
    this.isSuperAdmin = !!globalSettings.superAdmins.find(
      (user) =>
        user._id.toString() === this._authenticationService.user._id.toString()
    );

    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        this._syncPerformanceSubmenuWithRoute(event.urlAfterRedirects);
      });

    this._syncPerformanceSubmenuWithRoute(this._router.url);

    App.addListener('appUrlOpen', () => {
      this.mobileMenu.close();
    });
  }

  public ngOnInit() {
    this._userEntityService.updateUserFromBackend();
  }

  protected get currentRoute() {
    return this._router.url;
  }

  protected get appVersion() {
    return EnvironmentService.version;
  }

  protected logoutUser(event: any) {
    event.preventDefault();

    this._authenticationService.logout();
  }

  protected toggleSubmenu(submenu: ESubmenu) {
    if (this.openedMenu === submenu) {
      this.closedMenu = submenu;
      this.openedMenu = null;
      return;
    }

    this.closedMenu = null;
    this.openedMenu = submenu;
  }

  private _syncPerformanceSubmenuWithRoute(url: string): void {
    const urlWithoutQuery = url.split('?')[0];
    const segments = urlWithoutQuery.split('/').filter(Boolean);
    const lastSegment = segments.at(-1) ?? '';

    const isPerformanceSection = performanceSubmenuRoutes.includes(lastSegment);

    if (isPerformanceSection) {
      this.closedMenu = null;
      this.openedMenu = this.openedSubmenuType.PERFORMANCE;
      return;
    }

    if (this.openedMenu === this.openedSubmenuType.PERFORMANCE) {
      this.closedMenu = this.openedSubmenuType.PERFORMANCE;
      this.openedMenu = null;
    }
  }
}
